import { useState } from "react";
import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { Icon } from "../components/Icon";
import { Panel, PanelHead } from "../components/Panel";
import { RequestList } from "../components/RequestList";
import { Select, TextArea } from "../components/Field";
import { MilestoneStatusBadge, readinessLabel } from "../components/MilestoneStatus";
import { NEXT_TONE_CLASS } from "../data/status";
import { formatDate } from "../data/format";
import { updateMilestone } from "../data/api";
import { MILESTONE_STATUS_LABEL, MILESTONE_STATUS_ORDER } from "../../../src/shared/milestones";
import type { MilestoneDetail as MilestoneDetailShape, MilestoneStatus } from "../data/types";

export function MilestoneDetail() {
  // A 404 is thrown by the loader and handled by the route's errorElement, so
  // here the milestone always exists.
  const milestone = useLoaderData() as MilestoneDetailShape;
  const revalidator = useRevalidator();
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(milestone.detail);

  const patch = async (p: Parameters<typeof updateMilestone>[1]) => {
    setSaving(true);
    try {
      await updateMilestone(milestone.id, p);
      revalidator.revalidate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <Link to="/milestones" className="mb-4 inline-flex items-center gap-1 text-sm text-[var(--text-2)] hover:text-[var(--text)] focus-visible:focus-ring">
        <Icon name="arrow-left" />
        Milestones
      </Link>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-display-xs font-semibold text-gray-900">{milestone.title}</h1>
          <p className="mt-1 text-sm text-[var(--text-2)]">
            <Link to={`/accounts/${milestone.accountId}`} className="hover:text-[var(--text)] focus-visible:focus-ring">
              {milestone.account}
            </Link>
            {" · "}
            <span className={NEXT_TONE_CLASS[milestone.readiness.dateTone]}>{formatDate(milestone.eventDate)}</span>
          </p>
        </div>
        <MilestoneStatusBadge status={milestone.status} />
      </header>

      {/* One column, not the request page's two: the linked requests are a
          table, and squeezing one into a 1fr column beside a 280px aside
          collapses its headers into each other. The facts go in a strip. */}
      <dl className="mb-5 grid gap-4 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-4 sm:grid-cols-4">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Date</dt>
          <dd className={`mt-1 text-sm ${NEXT_TONE_CLASS[milestone.readiness.dateTone]}`}>{milestone.readiness.dateLabel}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Status</dt>
          <dd className="mt-1">
            <Select id="ms-status" aria-label="Status" value={milestone.status} disabled={saving} onChange={(e) => void patch({ status: e.target.value as MilestoneStatus })}>
              {MILESTONE_STATUS_ORDER.map((st) => (
                <option key={st} value={st}>
                  {MILESTONE_STATUS_LABEL[st]}
                </option>
              ))}
            </Select>
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Readiness</dt>
          <dd className="mt-1 text-sm">{readinessLabel(milestone.readiness)}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-[var(--text-3)]">Customer portal</dt>
          <dd className="mt-1">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={milestone.portalVisible} disabled={saving} onChange={(e) => void patch({ portalVisible: e.target.checked })} />
              Visible to the customer
            </label>
          </dd>
        </div>
      </dl>

      <div className="flex flex-col gap-5">
        <Panel labelledBy="ms-requests">
          <PanelHead id="ms-requests" title="Requests" count={milestone.requests.length} />
          <RequestList variant="staff" rows={milestone.requests} linkTo={(id) => `/requests/${id}`} emptyTitle="Nothing linked yet" emptyBody="Link requests from their own pages to track what has to land before this date." />
        </Panel>

        <Panel labelledBy="ms-notes">
          <PanelHead id="ms-notes" title="Notes" />
          <div className="flex flex-col gap-2 p-4">
            <TextArea
              id="ms-detail"
              aria-label="Milestone notes"
              rows={4}
              value={detail}
              disabled={saving}
              onChange={(e) => setDetail(e.target.value)}
              onBlur={() => {
                if (detail !== milestone.detail) void patch({ detail });
              }}
            />
            <p className="text-xs text-[var(--text-3)]">Internal. Customers never see this, even when the milestone is visible to them.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
